import { keyMatches } from "@shared/apiCacheRules";

/**
 * The browser's memory of dashboard reads (rules: shared/apiCacheRules.ts).
 * Memory only — nothing is written to storage, so signing out (a full page
 * load) or another account in the same browser starts empty.
 */

type Entry = { data: unknown; at: number };

const entries = new Map<string, Entry>();
const listeners = new Map<string, Set<() => void>>();
const CHANNEL = "sellhiz-api-cache";

export function readEntry(key: string): Entry | undefined {
  return entries.get(key);
}

/**
 * A screen gets its own copy: some sort or edit the lists they are handed, and
 * the remembered answer must not change under the next screen that asks.
 */
export function copyOf<T>(data: T): T {
  try {
    return structuredClone(data);
  } catch {
    return data;
  }
}

/** Remember an answer. Only a REPLACED entry tells its screens: a first load
 *  is already being awaited by the screen that asked. */
export function writeEntry(key: string, data: unknown): void {
  const replaced = entries.has(key);
  entries.set(key, { data: copyOf(data), at: Date.now() });
  if (replaced) for (const fn of listeners.get(key) ?? []) fn();
}

/** Re-render whoever showed `key` when it is refreshed or dropped. */
export function onEntryChange(key: string, fn: () => void): () => void {
  const set = listeners.get(key) ?? new Set();
  set.add(fn);
  listeners.set(key, set);
  return () => {
    set.delete(fn);
    if (!set.size) listeners.delete(key);
  };
}

function dropLocal(prefixes: string[]): void {
  const hit = [...entries.keys()].filter((k) => prefixes.some((p) => keyMatches(k, p)));
  for (const k of hit) entries.delete(k);
  for (const k of new Set([...hit, ...[...listeners.keys()].filter((k) => prefixes.some((p) => keyMatches(k, p)))])) {
    for (const fn of listeners.get(k) ?? []) fn();
  }
}

let channel: BroadcastChannel | null = null;
function bus(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === "undefined") return channel;
  channel = new BroadcastChannel(CHANNEL);
  // Node (tests) keeps a process alive for an open channel; browsers have no unref.
  (channel as unknown as { unref?: () => void }).unref?.();
  channel.onmessage = (e: MessageEvent<{ prefixes?: string[] }>) => {
    if (Array.isArray(e.data?.prefixes)) dropLocal(e.data.prefixes);
  };
  return channel;
}

/** Drop entries a save changed, here and in every other open tab. */
export function invalidateEntries(prefixes: string[]): void {
  if (!prefixes.length) return;
  dropLocal(prefixes);
  try {
    bus()?.postMessage({ prefixes });
  } catch {
    /* another tab simply refreshes on its own schedule */
  }
}

/** Forget exact keys, quietly: a screen's own "reload" must reach the network. */
export function forgetEntries(keys: string[]): void {
  for (const k of keys) entries.delete(k);
}

export function clearApiCache(): void {
  dropLocal(["/"]);
}

/** Listen for other tabs' saves from the moment the app starts. */
export function startApiCacheSync(): void {
  bus();
}

/**
 * Keys read while a `useFetch` fetcher runs, so the hook can follow them. Set
 * around the synchronous part of the fetcher only; a read after an await is not
 * followed, and simply refreshes on its own next mount.
 */
let collecting: Set<string> | null = null;

export function collectKeys<T>(run: () => T): { result: T; keys: string[] } {
  const prev = collecting;
  const keys = new Set<string>();
  collecting = keys;
  try {
    return { result: run(), keys: [...keys] };
  } finally {
    collecting = prev;
  }
}

export function noteKey(key: string): void {
  collecting?.add(key);
}
